import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatInr, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TrendingUp, Plus, Ban, AlertTriangle } from "lucide-react";
import {
    BILLING_CYCLES,
    PAYMENT_METHODS,
    PAYMENT_STATUSES,
    STATUS_TONE,
    SUBSCRIPTION_STATUSES,
    fetchShopOwners,
    todayIso,
} from "./shared";

export default function SubscriptionsPage() {
    const [rows, setRows] = useState(null);
    const [total, setTotal] = useState(0);
    const [packages, setPackages] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [filters, setFilters] = useState({ q: "", package_slug: "", status: "", payment_status: "", district_slug: "" });
    const [openSell, setOpenSell] = useState(false);

    const load = async () => {
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (filters.package_slug) params.set("package_slug", filters.package_slug);
            if (filters.status) params.set("status", filters.status);
            if (filters.payment_status) params.set("payment_status", filters.payment_status);
            if (filters.district_slug) params.set("district_slug", filters.district_slug);
            const res = await api.get(`/admin/subscriptions?${params.toString()}`);
            setRows(res.data.subscriptions);
            setTotal(res.data.total);
        } catch (e) {
            toast.error("Could not load subscriptions");
        }
    };

    useEffect(() => {
        Promise.all([api.get("/admin/packages"), api.get("/districts")]).then(
            ([p, d]) => {
                setPackages(p.data);
                setDistricts(d.data);
            },
        );
    }, []);
    useEffect(() => {
        load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.package_slug, filters.status, filters.payment_status, filters.district_slug]);

    const filtered = useMemo(() => {
        if (!rows) return null;
        const q = filters.q.toLowerCase().trim();
        if (!q) return rows;
        return rows.filter((r) => {
            const m = r.merchant || {};
            return (
                (m.business_name || "").toLowerCase().includes(q) ||
                (m.full_name || "").toLowerCase().includes(q) ||
                (m.mobile_number || "").includes(q)
            );
        });
    }, [rows, filters.q]);

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="subscriptions-page">
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                        Merchant billing
                    </div>
                    <h1 className="text-3xl font-extrabold text-neutral-900">Subscriptions</h1>
                    <p className="mt-1 text-sm text-neutral-500">{total} total · {rows?.filter((r) => r.status === "ACTIVE").length ?? 0} active</p>
                </div>
                <Button className="nest-gradient text-white" onClick={() => setOpenSell(true)} data-testid="sell-sub-btn">
                    <Plus size={16} className="mr-2" /> Sell subscription
                </Button>
            </header>

            <Card className="mb-4 rounded-2xl border border-neutral-200 p-4">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="sub-filters">
                    <Input
                        placeholder="Search business or mobile…"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        data-testid="sub-search"
                    />
                    <FilterSelect
                        placeholder="All packages"
                        value={filters.package_slug}
                        onChange={(v) => setFilters((f) => ({ ...f, package_slug: v }))}
                        options={[{ value: "__all__", label: "All packages" }, ...packages.map((p) => ({ value: p.slug, label: p.name }))]}
                        testid="sub-filter-package"
                    />
                    <FilterSelect
                        placeholder="Status"
                        value={filters.status}
                        onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        options={[{ value: "__all__", label: "All statuses" }, ...SUBSCRIPTION_STATUSES.map((s) => ({ value: s, label: s }))]}
                        testid="sub-filter-status"
                    />
                    <FilterSelect
                        placeholder="Payment"
                        value={filters.payment_status}
                        onChange={(v) => setFilters((f) => ({ ...f, payment_status: v }))}
                        options={[{ value: "__all__", label: "All payments" }, ...PAYMENT_STATUSES.map((s) => ({ value: s, label: s }))]}
                        testid="sub-filter-payment"
                    />
                    <FilterSelect
                        placeholder="District"
                        value={filters.district_slug}
                        onChange={(v) => setFilters((f) => ({ ...f, district_slug: v }))}
                        options={[{ value: "__all__", label: "All districts" }, ...districts.map((d) => ({ value: d.slug, label: d.name }))]}
                        testid="sub-filter-district"
                    />
                </div>
            </Card>

            <Card className="rounded-2xl border border-neutral-200">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-sm" data-testid="subs-table">
                        <thead>
                            <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                                <th className="px-4 py-3">Business</th>
                                <th className="px-4 py-3">Mobile</th>
                                <th className="px-4 py-3">Package</th>
                                <th className="px-4 py-3 text-right">Amount</th>
                                <th className="px-4 py-3">Start → End</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Payment</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered === null &&
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 8 }).map((_, j) => (
                                            <td key={j} className="px-4 py-3">
                                                <Skeleton className="h-4 w-full" />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            {filtered?.map((sub) => (
                                <SubRow key={sub.id} sub={sub} onChanged={load} />
                            ))}
                            {filtered?.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-neutral-500">
                                        No subscriptions match your filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <SellSubscriptionDialog
                open={openSell}
                onOpenChange={setOpenSell}
                packages={packages}
                onCreated={() => {
                    setOpenSell(false);
                    load();
                }}
            />
        </div>
    );
}

function SubRow({ sub, onChanged }) {
    const m = sub.merchant || {};
    const cancel = async () => {
        try {
            await api.post(`/admin/subscriptions/${sub.id}/cancel`);
            toast.success("Subscription cancelled");
            onChanged?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    return (
        <tr className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`sub-row-${sub.id}`}>
            <td className="px-4 py-3 font-semibold text-neutral-800">{m.business_name || m.full_name || "—"}</td>
            <td className="px-4 py-3 text-xs text-neutral-500">{m.mobile_number || "—"}</td>
            <td className="px-4 py-3">{sub.package_slug_snapshot}</td>
            <td className="px-4 py-3 text-right font-mono">{formatInr(sub.price_paise_snapshot)}</td>
            <td className="px-4 py-3 text-xs text-neutral-500">
                {formatDate(sub.start_date)} → {formatDate(sub.end_date)}
            </td>
            <td className="px-4 py-3">
                <Badge variant="outline" className={STATUS_TONE[sub.status]}>{sub.status}</Badge>
            </td>
            <td className="px-4 py-3">
                <Badge variant="outline" className={STATUS_TONE[sub.payment_status]}>{sub.payment_status}</Badge>
            </td>
            <td className="px-4 py-3 text-right">
                {sub.status === "ACTIVE" && (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" data-testid={`sub-cancel-${sub.id}`}>
                                <Ban size={14} className="mr-1" /> Cancel
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Cancel this subscription?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    The merchant will drop back to their fallback quota (100 coupons / month). This does not refund the payment.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Back</AlertDialogCancel>
                                <AlertDialogAction onClick={cancel}>Yes, cancel</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                )}
            </td>
        </tr>
    );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
    return (
        <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid={testid}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                        {o.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function SellSubscriptionDialog({ open, onOpenChange, packages, onCreated }) {
    const [merchants, setMerchants] = useState([]);
    const [merchantsError, setMerchantsError] = useState("");
    const [merchantId, setMerchantId] = useState("");
    const [merchantSearch, setMerchantSearch] = useState("");
    const [packageId, setPackageId] = useState("");
    const [billingCycle, setBillingCycle] = useState("MONTHLY");
    const [startDate, setStartDate] = useState(todayIso());
    const [paymentStatus, setPaymentStatus] = useState("PAID");
    const [paymentMethod, setPaymentMethod] = useState("UPI");
    const [paymentRef, setPaymentRef] = useState("");
    const [notes, setNotes] = useState("");
    const [existingSub, setExistingSub] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setMerchantsError("");
        fetchShopOwners()
            .then((list) => {
                setMerchants(Array.isArray(list) ? list : []);
            })
            .catch((err) => {
                const status = err?.response?.status;
                const msg =
                    status === 403
                        ? "You don't have permission to load the merchant list."
                        : "Failed to load merchants. Please try again.";
                setMerchantsError(msg);
                setMerchants([]);
                toast.error(msg);
            });
    }, [open]);

    useEffect(() => {
        if (!merchantId) {
            setExistingSub(null);
            return;
        }
        api
            .get(`/admin/subscriptions?merchant_user_id=${merchantId}&status=ACTIVE&limit=1`)
            .then((r) => setExistingSub(r.data.subscriptions[0] || null))
            .catch(() => setExistingSub(null));
    }, [merchantId]);

    const filteredMerchants = useMemo(() => {
        const q = merchantSearch.toLowerCase().trim();
        if (!q) return merchants.slice(0, 20);
        return merchants
            .filter((m) => m.status === "ACTIVE")
            .filter(
                (m) =>
                    (m.business_name || "").toLowerCase().includes(q) ||
                    (m.full_name || "").toLowerCase().includes(q) ||
                    (m.mobile_number || "").includes(q),
            )
            .slice(0, 20);
    }, [merchants, merchantSearch]);

    const submit = async () => {
        if (!merchantId || !packageId) {
            toast.error("Merchant and package are required.");
            return;
        }
        try {
            setBusy(true);
            await api.post("/admin/subscriptions", {
                merchant_user_id: merchantId,
                package_id: packageId,
                billing_cycle: billingCycle,
                start_date: startDate,
                payment_status: paymentStatus,
                payment_method: paymentMethod,
                payment_reference: paymentRef || null,
                paid_at: paymentStatus === "PAID" ? startDate : null,
                notes: notes || null,
            });
            toast.success("Subscription created");
            // reset
            setMerchantId("");
            setPackageId("");
            setPaymentRef("");
            setNotes("");
            onCreated?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="sell-sub-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TrendingUp size={16} className="text-orange-600" /> Sell subscription
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label className="text-xs">Merchant *</Label>
                        <Input
                            placeholder="Type to search (business / mobile / name)"
                            value={merchantSearch}
                            onChange={(e) => setMerchantSearch(e.target.value)}
                            data-testid="sell-sub-merchant-search"
                            disabled={!!merchantsError}
                        />
                        {merchantsError && (
                            <div
                                className="mt-1 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700"
                                data-testid="sell-sub-merchants-error"
                            >
                                {merchantsError}
                            </div>
                        )}
                        <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-neutral-200">
                            {filteredMerchants.map((m) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => {
                                        setMerchantId(m.id);
                                        setMerchantSearch(`${m.business_name || m.full_name} · ${m.mobile_number || m.email || ""}`);
                                    }}
                                    data-testid={`sell-sub-merchant-${m.id}`}
                                    className={`block w-full px-3 py-2 text-left text-sm hover:bg-orange-50 ${merchantId === m.id ? "bg-orange-100 font-semibold" : ""}`}
                                >
                                    <div className="font-medium">{m.business_name || m.full_name}</div>
                                    <div className="text-[11px] text-neutral-500">
                                        {m.mobile_number || m.email} · {m.business_district || "—"}
                                    </div>
                                </button>
                            ))}
                            {filteredMerchants.length === 0 && (
                                <div className="p-3 text-xs text-neutral-500">No merchants match.</div>
                            )}
                        </div>
                    </div>

                    {existingSub && (
                        <div
                            className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
                            data-testid="sell-sub-supersede-warn"
                        >
                            <AlertTriangle size={14} className="mt-0.5" />
                            <div>
                                This merchant already has an ACTIVE <b>{existingSub.package_slug_snapshot}</b> subscription
                                (until {formatDate(existingSub.end_date)}). Creating a new one will supersede it.
                            </div>
                        </div>
                    )}

                    <div>
                        <Label className="text-xs">Package *</Label>
                        <div className="grid grid-cols-3 gap-2" data-testid="sell-sub-package-picker">
                            {packages
                                .filter((p) => p.is_active)
                                .map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => setPackageId(p.id)}
                                        data-testid={`sell-sub-pkg-${p.slug}`}
                                        className={`rounded-xl border p-3 text-left transition ${packageId === p.id ? "border-orange-500 bg-orange-50" : "border-neutral-200 hover:border-orange-300"}`}
                                    >
                                        <div className="text-sm font-bold">{p.name}</div>
                                        <div className="mt-1 font-mono text-xs text-orange-700">
                                            {formatInr(p.monthly_price_paise)}/mo
                                        </div>
                                        <div className="mt-1 text-[10px] text-neutral-500">
                                            {p.features?.max_monthly_coupons_issued ?? "∞"} coupons/mo
                                        </div>
                                    </button>
                                ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Billing cycle</Label>
                            <Select value={billingCycle} onValueChange={setBillingCycle}>
                                <SelectTrigger data-testid="sell-sub-cycle"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {BILLING_CYCLES.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>
                                            {c.label} ({c.months} mo)
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Start date</Label>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="sell-sub-start" />
                        </div>
                        <div>
                            <Label className="text-xs">Payment status</Label>
                            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                                <SelectTrigger data-testid="sell-sub-payment-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PAYMENT_STATUSES.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Payment method</Label>
                            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                <SelectTrigger data-testid="sell-sub-payment-method"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PAYMENT_METHODS.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Payment reference</Label>
                            <Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="UPI txn ID / bank ref" data-testid="sell-sub-ref" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Notes</Label>
                            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sell-sub-notes" />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button className="nest-gradient text-white" disabled={busy || !!merchantsError} onClick={submit} data-testid="sell-sub-submit">
                        {busy ? "Creating…" : "Sell subscription"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
